import {
  useOrganisation,
  usePermissions,
  useUpdateOrganisationTmsWorkViewTabs,
} from '@my/directory-api/hooks';
import { Button, ErrorBanner, FormSection, Loading, Switch, showToast } from '@my/ui';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WORK_VIEW_PAGES, type WorkViewPage } from '@/stores/workView';
import {
  parseWorkViewTabs,
  serialiseWorkViewTabs,
  type WorkViewTabMap,
} from '@/features/views/workViewTabs';

export interface WorkViewTabsSectionProps {
  /** Owning organisation id; the card stays disabled while this is null. */
  organisationId: string | null;
}

/**
 * Which tabs the Work View (`/orders`) offers this organisation.
 *
 * <p>
 * STRATUM SUPPORT ONLY, AND IT ASKS HERE. Returning null for everybody else
 * keeps the decision with the component that knows why, so Central
 * Configuration reads as the list of cards it is; opening this up to Owners
 * later is deleting the guard below, not unpicking a condition threaded
 * through the page. The endpoint enforces the same rule and answers 404 to a
 * customer token — this is the affordance, not the defence.
 * </p>
 *
 * <p>
 * Not a capability check. `AppCapabilities` answers "may THIS USER reach this
 * area", ceiling-set by their platform role; this is "does this ORGANISATION
 * have this feature", which is a different axis — support is deciding it on
 * the customer's behalf, and every user in the org gets the same answer.
 * </p>
 *
 * <p>
 * The draft is the complete map, always, and Save writes every tab. A sparse
 * payload would save a few bytes and lose the difference between a tab that is
 * off and one that did not exist when the row was written.
 * </p>
 */
export function WorkViewTabsSection({ organisationId }: WorkViewTabsSectionProps) {
  const { t } = useTranslation(['configuration', 'common']);
  const { role } = usePermissions();
  const organisationQuery = useOrganisation(organisationId);
  const saveMutation = useUpdateOrganisationTmsWorkViewTabs();

  const stored = useMemo(
    () => parseWorkViewTabs(organisationQuery.data?.tmsWorkViewTabsJson),
    [organisationQuery.data?.tmsWorkViewTabsJson],
  );

  const [draft, setDraft] = useState<WorkViewTabMap>(stored);

  // Re-seed whenever the server's copy changes — the first load, and after a
  // save invalidates the query. Keyed on the serialised map rather than the
  // object so a refetch returning identical data does not throw away an edit
  // in progress.
  const storedKey = serialiseWorkViewTabs(stored);
  useEffect(() => {
    setDraft(parseWorkViewTabs(storedKey));
  }, [storedKey]);

  const enabledCount = WORK_VIEW_PAGES.filter((page) => draft[page]).length;
  const isDirty = serialiseWorkViewTabs(draft) !== storedKey;
  const isSaveable = isDirty && enabledCount > 0 && !!organisationId;

  if (role !== 'SuperUser') return null;

  const toggle = (page: WorkViewPage, next: boolean) =>
    setDraft((prev) => ({ ...prev, [page]: next }));

  const handleSave = async () => {
    if (!organisationId) return;
    try {
      await saveMutation.mutateAsync({
        organisationId,
        tabsJson: serialiseWorkViewTabs(draft),
      });
      showToast({ type: 'success', message: t('configuration:workViewTabs.saved') });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : t('configuration:workViewTabs.saveFailed'),
      });
    }
  };

  return (
    <FormSection
      title={t('configuration:workViewTabs.title')}
      columns={3}
      disabled={!organisationId}
      footer={
        <div className="flex items-center gap-3">
          {/* The rule the BE also enforces, said before the click rather than
              after it. Save stays disabled, so this explains the disablement
              instead of leaving it a mystery. */}
          {enabledCount === 0 && (
            <span className="text-sm text-red-600">
              {t('configuration:workViewTabs.atLeastOne')}
            </span>
          )}
          <Button
            type="button"
            disabled={!isSaveable}
            loading={saveMutation.isPending}
            onClick={handleSave}
          >
            {t('common:actions.save')}
          </Button>
        </div>
      }
    >
      {/* Full-width lead line. Says out loud that this card is ours, so a
          support user reading it knows the customer is not looking at the
          same screen. */}
      <p className="text-sm text-slate-500 md:col-span-3">
        {t('configuration:workViewTabs.description')}
      </p>

      {organisationQuery.isPending ? (
        <div className="md:col-span-3">
          <Loading label={t('common:states.loading')} />
        </div>
      ) : organisationQuery.isError ? (
        <div className="md:col-span-3">
          <ErrorBanner
            title={t('configuration:workViewTabs.loadFailed')}
            message={organisationQuery.error.message}
          />
        </div>
      ) : (
        WORK_VIEW_PAGES.map((page) => (
          <Switch
            key={page}
            name={`workViewTab-${page}`}
            label={t(`configuration:workViewTabs.tabs.${page}`)}
            checked={draft[page]}
            enabledLabel={t('common:states.active')}
            disabledLabel={t('common:states.inactive')}
            onChange={(e) => toggle(page, e.target.checked)}
          />
        ))
      )}
    </FormSection>
  );
}
